<script>
    import { onMount, onDestroy } from "svelte";
    import { Link, navigate, useLocation } from "svelte-routing";
    import allContent from "../data/content.json";

    let isOpen = false;
    let isSearchOpen = false;
    let searchTerm = "";
    let searchPanel;
    let searchInput;
    let isLight = false;
    let observer;
    let scrollY = 0;

    const location = useLocation();

    $: isScrolled = scrollY > 50;

    const navItems = [
        { label: "수학", to: "/mathpage" },
        { label: "자연 과학", to: "/sciencepage" },
        { label: "컴퓨터 과학", to: "/cspage" }
    ];

    $: quickResults = allContent
        .filter((item) => {
            const keyword = searchTerm.trim().toLowerCase();
            if (!keyword) {
                return true;
            }

            const haystack = `${item.title} ${item.description} ${item.tags.join(" ")}`.toLowerCase();
            return haystack.includes(keyword);
        })
        .slice(0, 4);

    $: isHomepage = $location.pathname === "/";

    function updateObserver(homepage) {
        if (observer) {
            observer.disconnect();
            observer = null;
        }

        if (homepage) {
            setTimeout(() => {
                const blogSection = document.getElementById("blog-section");
                if (blogSection) {
                    observer = new IntersectionObserver(
                        (entries) => {
                            isLight = entries[0].isIntersecting;
                        },
                        { threshold: 0.02, rootMargin: "-60px 0px 0px 0px" }
                    );
                    observer.observe(blogSection);
                } else {
                    isLight = false;
                }
            }, 50);
        } else {
            isLight = true;
        }
    }

    $: updateObserver(isHomepage);

    function toggleMenu() {
        isOpen = !isOpen;
    }

    function toggleSearch() {
        isSearchOpen = !isSearchOpen;

        if (isSearchOpen) {
            isOpen = false;
            setTimeout(() => searchInput && searchInput.focus(), 0);
        }
    }

    function closeSearch() {
        isSearchOpen = false;
        searchTerm = "";
    }

    function goToContent(id) {
        closeSearch();
        navigate(`/content/${id}`);
    }

    function handleClickOutside(event) {
        if (isSearchOpen && searchPanel && !searchPanel.contains(event.target)) {
            closeSearch();
        }
    }

    function stopPropagation() {}

    onMount(() => {
        window.addEventListener("click", handleClickOutside);
    });

    onDestroy(() => {
        window.removeEventListener("click", handleClickOutside);
        if (observer) observer.disconnect();
    });
</script>

<svelte:window bind:scrollY={scrollY} />

<header class="site-header" class:scrolled={!isHomepage || isScrolled} class:dark-mode={isHomepage && !isScrolled}>
    <div class="nav-shell">
        <div class="left-section">
            <button class="menu-btn" on:click={toggleMenu} aria-label="Toggle menu" aria-expanded={isOpen}>
                <span class="material-symbols-outlined">menu</span>
            </button>
            <Link to="/" class="brand-link">WebLab</Link>
        </div>
        
        <div class="right-section">
            <nav class="desktop-nav">
                {#each navItems as item}
                    <Link to={item.to} class="nav-link">{item.label}</Link>
                {/each}
            </nav>
            <div class="divider"></div>
            <button class="search-btn" class:active={isSearchOpen} type="button" on:click|stopPropagation={toggleSearch}>
                <span class="material-symbols-outlined search-icon" style={isSearchOpen ? "font-variation-settings: 'FILL' 1;" : ""}>search</span>
            </button>
        </div>
    </div>

    <!-- Search Dropdown Panel -->
    {#if isSearchOpen}
        <div class="search-panel-wrapper" on:click|stopPropagation on:keydown={stopPropagation}>
            <div class="search-panel" bind:this={searchPanel}>
                <!-- Modern Search Input -->
                <div class="search-input-container">
                    <span class="material-symbols-outlined input-icon">search</span>
                    <input
                        bind:this={searchInput}
                        bind:value={searchTerm}
                        class="search-input"
                        type="text"
                        placeholder="Search math formulas, physics principles, or algorithms..."
                        aria-label="Search content"
                    />
                    <button class="close-search-btn" on:click={closeSearch}>
                        <span class="material-symbols-outlined">close</span>
                    </button>
                </div>

                <!-- Search Results Grid -->
                <div class="search-results-container">
                    <div class="results-header">
                        <h3 class="results-title">Recent & Relevant</h3>
                        {#if quickResults.length > 0}
                            <span class="results-count">View all results</span>
                        {/if}
                    </div>
                    <div class="results-grid">
                        {#each quickResults as item}
                            <button class="result-card" type="button" on:click={() => goToContent(item.id)}>
                                <div class="result-card-bg"></div>
                                <div class="result-content">
                                    <h4 class="result-title">{item.title}</h4>
                                    <p class="result-desc">{item.description}</p>
                                    <div class="result-tags">
                                        {#each item.tags.slice(0, 3) as tag}
                                            <span class="tag">{tag}</span>
                                        {/each}
                                    </div>
                                </div>
                            </button>
                        {/each}
                        {#if quickResults.length === 0}
                            <div class="empty-state">No matching content found.</div>
                        {/if}
                    </div>
                </div>
            </div>
        </div>
    {/if}

    <!-- Mobile Drawer overlay -->
    {#if isOpen}
        <div class="drawer-overlay open" on:click={toggleMenu} on:keydown={stopPropagation} tabindex="0" role="button"></div>
    {/if}

    <!-- Mobile Drawer menu -->
    <div class="mobile-drawer" class:open={isOpen}>
        <div class="mobile-drawer-header">
            <span class="brand-link">WebLab</span>
            <button class="close-menu" on:click={toggleMenu}>
                <span class="material-symbols-outlined">close</span>
            </button>
        </div>
        <div class="mobile-nav">
            {#each navItems as item}
                <Link to={item.to} class="mobile-nav-link" on:click={toggleMenu}>{item.label}</Link>
            {/each}
        </div>
    </div>
</header>

<style>
    .site-header {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        z-index: 50;
        background-color: rgba(255, 255, 255, 0.7);
        backdrop-filter: blur(12px);
        -webkit-backdrop-filter: blur(12px);
        border-bottom: 1px solid rgba(0, 0, 0, 0.05);
        box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
        transition: all 0.3s ease-in-out;
        height: 64px;
    }

    .site-header.scrolled {
        background-color: rgba(255, 255, 255, 0.95);
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    }

    .site-header.dark-mode {
        background-color: transparent;
        border-bottom-color: transparent;
        box-shadow: none;
        backdrop-filter: none;
        -webkit-backdrop-filter: none;
    }

    .site-header.dark-mode .menu-btn {
        color: rgba(255, 255, 255, 0.9);
    }

    .site-header.dark-mode .menu-btn:hover {
        background-color: rgba(255, 255, 255, 0.1);
    }

    .site-header.dark-mode :global(a.brand-link),
    .site-header.dark-mode .brand-link {
        color: #ffffff;
    }

    .site-header.dark-mode :global(a.nav-link) {
        color: rgba(255, 255, 255, 0.8);
    }

    .site-header.dark-mode :global(a.nav-link:hover) {
        color: #ffffff;
        background-color: rgba(255, 255, 255, 0.1);
    }

    .site-header.dark-mode .divider {
        background-color: rgba(255, 255, 255, 0.2);
    }

    .site-header.dark-mode .search-btn:not(.active) {
        color: rgba(255, 255, 255, 0.8);
    }

    .site-header.dark-mode .search-btn:not(.active):hover {
        color: #ffffff;
        background-color: rgba(255, 255, 255, 0.1);
    }

    /* Search Dropdown Dark Mode Overrides */
    .site-header.dark-mode .search-panel-wrapper {
        background-color: rgba(0, 0, 0, 0.8);
        border-bottom-color: rgba(255, 255, 255, 0.1);
        backdrop-filter: blur(12px);
        -webkit-backdrop-filter: blur(12px);
    }

    .site-header.dark-mode .search-input {
        background-color: rgba(255, 255, 255, 0.1);
        color: #ffffff;
    }

    .site-header.dark-mode .search-input::placeholder {
        color: rgba(255, 255, 255, 0.5);
    }

    .site-header.dark-mode .input-icon,
    .site-header.dark-mode .close-search-btn {
        color: rgba(255, 255, 255, 0.7);
    }

    .site-header.dark-mode .close-search-btn:hover {
        color: #ffffff;
    }

    .site-header.dark-mode .results-title {
        color: rgba(255, 255, 255, 0.7);
    }

    .site-header.dark-mode .result-card {
        background-color: rgba(255, 255, 255, 0.05);
        border-color: rgba(255, 255, 255, 0.1);
    }

    .site-header.dark-mode .result-card:hover {
        border-color: rgba(15, 98, 254, 0.5);
        background-color: rgba(255, 255, 255, 0.1);
    }

    .site-header.dark-mode .result-title {
        color: #ffffff;
    }

    .site-header.dark-mode .result-desc {
        color: rgba(255, 255, 255, 0.7);
    }

    .site-header.dark-mode .tag {
        background-color: rgba(255, 255, 255, 0.1);
        color: rgba(255, 255, 255, 0.8);
    }

    .site-header.dark-mode .empty-state {
        color: rgba(255, 255, 255, 0.7);
    }

    .nav-shell {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0 24px;
        height: 100%;
        max-width: 1280px;
        margin: 0 auto;
    }

    .left-section {
        display: flex;
        align-items: center;
        gap: 4px;
    }

    .menu-btn {
        padding: 8px;
        color: #121417;
        background: transparent;
        border: none;
        border-radius: 9999px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: background-color 0.2s;
    }

    .menu-btn:hover {
        background-color: rgba(0, 0, 0, 0.05);
    }

    :global(a.brand-link), .brand-link {
        font-size: 1.25rem;
        font-family: "Inter", sans-serif;
        font-weight: 900;
        color: #121417;
        letter-spacing: -0.025em;
        text-decoration: none;
    }

    .right-section {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .desktop-nav {
        display: none;
        gap: 4px;
    }

    @media (min-width: 768px) {
        .desktop-nav {
            display: flex;
        }
    }

    :global(a.nav-link) {
        padding: 8px 16px;
        color: #66707a;
        border-radius: 9999px;
        font-family: "Inter", sans-serif;
        font-size: 0.875rem;
        font-weight: 500;
        text-decoration: none;
        transition: all 0.2s;
    }

    :global(a.nav-link:hover) {
        color: #0f62fe;
        background-color: rgba(0, 0, 0, 0.05);
    }

    .divider {
        display: none;
        height: 24px;
        width: 1px;
        background-color: rgba(0, 0, 0, 0.1);
        margin: 0 8px;
    }

    @media (min-width: 768px) {
        .divider {
            display: block;
        }
    }

    .search-btn {
        padding: 8px;
        color: #0f62fe;
        background: rgba(15, 98, 254, 0.1);
        border: none;
        border-radius: 9999px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.2s;
    }
    
    .search-btn:not(.active) {
        color: #66707a;
        background: transparent;
    }

    .search-btn:not(.active):hover {
        color: #121417;
        background-color: rgba(0, 0, 0, 0.05);
    }

    /* Search Dropdown Panel */
    .search-panel-wrapper {
        position: absolute;
        top: 100%;
        left: 0;
        width: 100%;
        background-color: #ffffff;
        border-bottom: 1px solid rgba(0, 0, 0, 0.05);
        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
        max-height: 819px;
        overflow-y: auto;
    }

    .search-panel {
        max-width: 1280px;
        margin: 0 auto;
        padding: 24px;
    }

    @media (min-width: 768px) {
        .search-panel {
            padding: 32px;
        }
    }

    .search-input-container {
        position: relative;
        max-width: 48rem; /* max-w-3xl */
        margin: 0 auto 40px;
    }

    .input-icon {
        position: absolute;
        left: 16px;
        top: 50%;
        transform: translateY(-50%);
        color: #66707a;
        pointer-events: none;
    }

    .search-input {
        width: 100%;
        background-color: #f1f3f5;
        border: none;
        border-radius: 9999px;
        padding: 16px 48px;
        color: #121417;
        font-family: "Inter", sans-serif;
        font-size: 1rem;
        outline: none;
        transition: box-shadow 0.2s;
    }

    .search-input::placeholder {
        color: #66707a;
    }

    .search-input:focus {
        box-shadow: 0 0 0 2px #0f62fe;
    }

    .close-search-btn {
        position: absolute;
        right: 16px;
        top: 50%;
        transform: translateY(-50%);
        color: #66707a;
        background: transparent;
        border: none;
        cursor: pointer;
        display: flex;
        align-items: center;
        transition: color 0.2s;
    }

    .close-search-btn:hover {
        color: #121417;
    }

    .search-results-container {
        max-width: 64rem; /* max-w-5xl */
        margin: 0 auto;
    }

    .results-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 16px;
    }

    .results-title {
        font-size: 0.875rem;
        font-weight: 700;
        color: #66707a;
        text-transform: uppercase;
        letter-spacing: 0.05em;
    }

    .results-count {
        font-size: 0.875rem;
        font-weight: 500;
        color: #0f62fe;
        cursor: pointer;
    }

    .results-grid {
        display: grid;
        grid-template-columns: 1fr;
        gap: 16px;
    }

    @media (min-width: 768px) {
        .results-grid {
            grid-template-columns: repeat(2, 1fr);
        }
    }

    @media (min-width: 1024px) {
        .results-grid {
            grid-template-columns: repeat(3, 1fr);
        }
    }

    .result-card {
        text-align: left;
        background-color: #ffffff;
        border: 1px solid rgba(0, 0, 0, 0.05);
        border-radius: 0.5rem;
        padding: 20px;
        cursor: pointer;
        position: relative;
        overflow: hidden;
        transition: all 0.3s;
    }

    .result-card:hover {
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        border-color: rgba(15, 98, 254, 0.3);
    }

    .result-card-bg {
        position: absolute;
        top: 0;
        right: 0;
        width: 96px;
        height: 96px;
        background: linear-gradient(to bottom left, #eff6ff, transparent);
        opacity: 0.5;
        border-bottom-left-radius: 9999px;
        transition: transform 0.3s;
    }

    .result-card:hover .result-card-bg {
        transform: scale(1.1);
    }

    .result-title {
        font-weight: 700;
        color: #121417;
        margin-bottom: 4px;
        transition: color 0.2s;
        display: -webkit-box;
        -webkit-line-clamp: 1;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }

    .result-card:hover .result-title {
        color: #0f62fe;
    }

    .result-desc {
        font-size: 0.875rem;
        color: rgba(18, 20, 23, 0.8);
        font-family: "Inter", sans-serif;
        margin-top: 8px;
        margin-bottom: 16px;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }

    .result-tags {
        display: flex;
        gap: 8px;
        position: relative;
        z-index: 1;
    }

    .tag {
        padding: 4px 8px;
        background-color: rgba(0, 0, 0, 0.05);
        font-size: 0.75rem;
        color: #66707a;
        border-radius: 0.25rem;
        font-weight: 500;
    }

    .empty-state {
        grid-column: 1 / -1;
        text-align: center;
        padding: 40px;
        color: #66707a;
    }

    /* Mobile Drawer */
    .drawer-overlay {
        display: block;
        position: fixed;
        inset: 0;
        background: rgba(17, 24, 39, 0.4);
        backdrop-filter: blur(2px);
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.35s ease;
        z-index: 60;
    }

    .drawer-overlay.open {
        opacity: 1;
        pointer-events: auto;
    }

    .mobile-drawer {
        position: fixed;
        top: 0;
        left: -280px;
        width: 280px;
        height: 100vh;
        background: #ffffff;
        border-right: 1px solid rgba(0, 0, 0, 0.05);
        display: flex;
        flex-direction: column;
        padding: 16px;
        transition: transform 0.3s ease;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        z-index: 70;
    }

    .mobile-drawer.open {
        transform: translateX(280px);
    }

    .mobile-drawer-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 24px;
        padding-bottom: 16px;
        border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    }

    .close-menu {
        background: transparent;
        border: none;
        color: #66707a;
        cursor: pointer;
        padding: 8px;
        border-radius: 50%;
        transition: background 0.2s ease;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .close-menu:hover {
        background: rgba(0, 0, 0, 0.05);
    }

    .mobile-nav {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    :global(a.mobile-nav-link) {
        text-align: left;
        width: 100%;
        padding: 14px 16px;
        border-radius: 12px;
        font-size: 0.94rem;
        color: #66707a;
        text-decoration: none;
        font-weight: 500;
        transition: all 0.2s;
    }

    :global(a.mobile-nav-link:hover) {
        color: #121417;
        background-color: rgba(0, 0, 0, 0.05);
    }
</style>
